
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import geometry from 'client/utils/format/ol/geometry';
import { ClientWithAuth, setCustomRequestId } from 'client/services/service';
import { aoiLayerChunkSize, aoiLayerMaxThread } from 'client/utils/constants/frontEndConfig';
import PQueue from 'p-queue';
import { getValueFromMapConfig, getEpsgCode } from 'client/utils/helpers/MapConfig/MapConfig';
import { v4 as uuid } from 'uuid';

const serviceUrlSuffix = '/asset-request-service';
const cachedFeatures = new Map();

const clearFeatureCache = () => {
    cachedFeatures.clear();
};

// Depending upon what has been marshalled/unmarshalled the coordinate resolution will hava changed
// therefore normalise to 8 decimal places so 'values that follow this pattern' are seen as the 'same' in string comparisons
const getAOIAsCoordinatesString = (aoi) => {
    return aoi.getGeometry().getCoordinates().flat(Infinity).map(coord => coord.toFixed(8)).toString();
};

const isSameAOI = (newAoiCoords, cachedAoiCoords) => {
    return newAoiCoords === cachedAoiCoords;
};

const makeFeaturesRequest = async (aoi, typeNames = null, requestId, buffer) => {
    if (!aoi || !(aoi instanceof Feature)) {
        throw new Error('aoi is NOT instance of ol/Feature');
    }

    // Keep a cache of types to coordinates and result set
    // The cache will 'always exist' when created.
    // The cache values will change even if the key is the same if the coordinates are new
    // Hence the cache value holds, as a string, coordinates of the aoi and the result value
    const newCoords = getAOIAsCoordinatesString(aoi);
    const featuresForTypesCacheKey = JSON.stringify({ typeNames, buffer: buffer ?? null });
    const cachedFeatureRequest = cachedFeatures.get(featuresForTypesCacheKey);
    if (cachedFeatureRequest && isSameAOI(newCoords, cachedFeatureRequest?.coords)) {
        return cachedFeatureRequest.cache;
    }

    const aoiGeometry = aoi.getGeometry();
    const wktGeometry = geometry.toWKT(aoiGeometry);

    // Backend requires a buffer for Point and LineString geometries
    const isPointOrLine = wktGeometry.startsWith('POINT') ||
        wktGeometry.startsWith('LINESTRING') ||
        wktGeometry.startsWith('MULTILINESTRING');
    const effectiveBuffer = buffer != null ? buffer : (isPointOrLine ? 10 : undefined);

    const data = {
        wktGeometry,
        typeNames,
        ...(effectiveBuffer != null && { buffer: effectiveBuffer })
    };

    const epsgCode = await getEpsgCode();
    const headers = setCustomRequestId(ClientWithAuth.defaults.headers, requestId);
    const response = await ClientWithAuth.post(`${serviceUrlSuffix}/${epsgCode}/aoi`, data, { headers });
    const featuresByTypeName = response?.data?.features;
    const geoJson = new GeoJSON();

    const features = {};

    Object.keys(featuresByTypeName).forEach(typeName => {
        features[typeName] = geoJson.readFeatures(featuresByTypeName[typeName]);
    });

    // add to cache
    const cachedFeatureResponse = {
        features,
        sensitiveFeatures: [...response?.data?.sensitiveFeatures]
    };
    cachedFeatures.set(featuresForTypesCacheKey, { cache: cachedFeatureResponse, coords: newCoords });
    return cachedFeatureResponse;
};

const typeNamesToBatches = typeNames => {
    const chunkSize = parseInt(aoiLayerChunkSize);
    const batches = [];
    const layers = typeNames.map(i => 'nuar:' + i);
    for (let i = 0; i < layers.length; i += chunkSize) {
        batches.push(layers.slice(i, i + chunkSize));
    }
    return batches;
};

const getFeatures = async (aoi, onBatchComplete = () => { }, { buffer } = {}) => {
    const typeNames = await getValueFromMapConfig(state => {
        const typeNames = state.layers?.result?.typeNames;
        if (typeNames.length) {
            // Store starts out with typeNames populated but length zero
            // we need to wait until they have actually loaded.
            return typeNames;
        }
    });

    return new Promise((resolve, reject) => {
        if (typeNames?.length <= 0) {
            reject(new Error('No layer types found'));
            return;
        }
        if (!aoi || !(aoi instanceof Feature)) {
            reject(new Error('aoi is NOT instance of ol/Feature'));
            return;
        }

        let batchError = false;
        const batches = typeNamesToBatches(typeNames);
        const maxThread = parseInt(aoiLayerMaxThread);
        const queue = new PQueue({ concurrency: maxThread });

        const results = {
            features: {},
            sensitiveFeatures: []
        };
        queue.on('idle', () => {
            // 'idle' here actually means: when everything added to the queue is complete
            // but not necessarily successfully complete, hence also check for failures.
            if (batchError) {
                reject(batchError);
                return;
            }
            // Constuct the feature ID list from the aggregated results
            const featureIdList = [];
            Object.keys(results.features).forEach(typeName => {
                results.features[typeName].forEach(result => featureIdList.push({
                    dataProviderAssignedUniqueId: result.values_?.dataproviderassigneduniqueid,
                    dataProviderId: result.values_?.dataproviderid_fk
                }));
            });
            results.sensitiveFeatures.forEach(result => featureIdList.push({
                dataProviderAssignedUniqueId: result?.dataproviderassigneduniqueid,
                dataProviderId: result?.dataproviderid_fk
            }));

            resolve({
                ...results,
                featureIdList
            });
        });

        const requestId = uuid();
        for (const batch of batches) {
            queue.add(() => {
                // return on new line to avoid easy bug queue.add(makeFeaturesRequest(aoi, batch)).then(result....
                // instead of queue.add(makeFeaturesRequest(aoi, batch).then(result....
                return makeFeaturesRequest(aoi, batch, requestId, buffer).then(result => {
                    if (batchError) {
                        // a different call might have failed before this one resolved
                        // so only handle this success if no other batch has already failed.
                        return;
                    }

                    // Call the batch handler with this specific result
                    onBatchComplete(result);

                    // Aggregate the results so we can resolve with the full set when all done
                    // (for consumers who don't want to handle incrementally, e.g view i2d)
                    results.features = {
                        ...results.features,
                        ...result.features
                    };
                    results.sensitiveFeatures = [
                        ...results.sensitiveFeatures,
                        ...result.sensitiveFeatures
                    ];
                }).catch((err) => {
                    batchError = err;
                });
            });
        }
    });
};

const getEnquiryFeatures = async (geometry) => {
    const aoi = new GeoJSON().readFeature({ geometry, type: 'Feature' });
    const results = await getFeatures(aoi);
    const featuresWithoutObservations = {
        ...results.features,
        observation: []
    };
    const organisationsWithEnhancedMeasures = new Set();
    if (results.sensitiveFeatures.length > 0) {
        results.sensitiveFeatures.forEach(enhancedMeasure => organisationsWithEnhancedMeasures.add(enhancedMeasure.dataproviderid_fk));
    }
    return { aoi, featuresByAssetType: featuresWithoutObservations, organisationsWithEnhancedMeasures, featureIdList: results.featureIdList, sensitiveFeatures: results.sensitiveFeatures };
};

const getSupplementaryData = async (dataProviderList) => {
    try {
        const epsgCode = await getEpsgCode();
        const response = await ClientWithAuth.post(`${serviceUrlSuffix}/${epsgCode}/asset/supplementary`, { dataProviderList });
        return response.data;
    } catch (e) {
        throw new Error('Supplementary data did not load');
    }
};

const downloadLinkedFile = async (systemId, artefactTable, artefactId) => {
    const epsgCode = await getEpsgCode();
    const config = { responseType: 'blob' };
    try {
        const response = await ClientWithAuth.get(`${serviceUrlSuffix}/${epsgCode}/linkedfile/${systemId}?linkedObjectTable=${artefactTable}&linkedObjectId=${artefactId}`, config);
        return response.data;
    } catch (e) {
        return null;
    }
};

export default {
    getFeatures,
    clearFeatureCache,
    getEnquiryFeatures,
    getSupplementaryData,
    downloadLinkedFile
};
